import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDashboardToast } from '../DashboardToastProvider';
import { useClientContext } from '../ClientContext';
import { userStorage } from '../../utils';
import { useClientTokenManager } from '../ClientTokenContext';
const initialValue = {
    actions: {},
    client: {
        id: '',
        name: '',
        entities: [],
        data: {
            client_id: '',
            name: '',
            entities: [],
        },
    },
    selectedAccountLevel: 'client',
};
export const CurrentClientManagerContext = createContext(initialValue);
export const useCurrentClientManager = () => useContext(CurrentClientManagerContext);
export const CurrentClientProvider = ({ children, defaultAvailableEntities, defaultAvailableSegments, defaultClientSegments, defaultClient, defaultEntity, defaultSegment, defaultSubEntity, }) => {
    const [client, setClient] = useState(defaultClient || initialValue.client);
    const [entity, setEntity] = useState(defaultEntity);
    const [segment, setSegment] = useState(defaultSegment);
    const [subEntity] = useState(defaultSubEntity);
    const [availableEntities, setAvailableEntities] = useState(defaultAvailableEntities || []);
    const [availableSegments, setAvailableSegments] = useState(defaultAvailableSegments || []);
    const [availableClientSegments, setAvailableClientSegements] = useState(defaultClientSegments || []);
    const { actions: { refreshToken }, tokenProperties: { userId }, } = useClientTokenManager();
    const { showToast } = useDashboardToast();
    const { getClient } = useClientContext();
    const showChangeViewFailedToast = useCallback(() => {
        showToast('There was a problem switching accounts, please retry.', { variant: 'alert' });
        // do not incldue showToast in dependency array as it will cause unnecessary re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const setNewClientEntity = useCallback((newClientEntity) => {
        if (newClientEntity.client) {
            setClient(newClientEntity.client);
            userStorage.setClient(newClientEntity.client);
        }
        if (newClientEntity.entity) {
            setEntity(newClientEntity.entity);
            userStorage.setEntity(newClientEntity.entity);
        }
        else {
            setEntity(undefined);
            userStorage.clearEntity();
        }
        if (newClientEntity.segment) {
            setSegment(newClientEntity.segment);
            userStorage.setSegment(newClientEntity.segment);
        }
        else {
            setSegment(undefined);
            userStorage.clearSegment();
        }
        if (newClientEntity.availableEntities) {
            setAvailableEntities(newClientEntity.availableEntities);
        }
        else {
            setAvailableEntities([]);
        }
        if (newClientEntity.availableClientSegments) {
            setAvailableClientSegements(newClientEntity.availableClientSegments);
        }
        else {
            setAvailableClientSegements([]);
        }
        if (newClientEntity.availableSegments) {
            setAvailableSegments(newClientEntity.availableSegments);
        }
        else {
            setAvailableSegments([]);
        }
    }, []);
    const changeView = useCallback(async (updatedView) => {
        if (!updatedView || !updatedView.client) {
            return undefined;
        }
        const isSameClient = client?.id === updatedView?.client?.id;
        const isSameEntity = entity?.id === updatedView?.entity?.id;
        const isSameSegment = segment?.id === updatedView?.segment?.id;
        if (isSameClient && isSameEntity && isSameSegment) {
            return undefined;
        }
        let clientAndEntities;
        let clientSegments;
        if (isSameClient) {
            clientAndEntities = client;
            clientSegments = availableClientSegments;
        }
        else {
            try {
                clientAndEntities = await getClient(updatedView.client.id);
            }
            catch (error) {
                // eslint-disable-next-line no-console
                console.error('Could not fetch clients and entities when updating the account selected view.');
                showChangeViewFailedToast();
                return false;
            }
            if (clientAndEntities !== undefined) {
                clientSegments = clientAndEntities.entities.flatMap((entityModel) => entityModel.entitySegments);
            }
            else {
                // eslint-disable-next-line no-console
                console.error('Could not fetch clients and entities when updating the account selected view.');
                showChangeViewFailedToast();
                return false;
            }
        }
        const segments = updatedView.entity
            ? clientSegments.filter((clientSegment) => clientSegment.entityId === updatedView.entity?.id)
            : [...clientSegments];
        const selectedMerchant = {
            availableEntities: clientAndEntities.entities,
            availableSegments: segments,
            availableClientSegments: clientSegments,
            client: clientAndEntities,
            entity: updatedView.entity,
            segment: updatedView.segment,
        };
        if (!isSameClient) {
            await refreshToken(selectedMerchant?.client?.id || '');
        }
        setNewClientEntity(selectedMerchant);
        return true;
    }, [
        availableClientSegments,
        client,
        entity?.id,
        getClient,
        refreshToken,
        segment?.id,
        setNewClientEntity,
        showChangeViewFailedToast,
    ]);
    const setSelectedAccountLevel = () => {
        if (subEntity) {
            return 'sub-entity';
        }
        if (segment) {
            return 'segment';
        }
        if (entity) {
            return 'entity';
        }
        return 'client';
    };
    const selectedAccountLevel = setSelectedAccountLevel();
    const values = useMemo(() => ({
        actions: {
            setNewClientEntity,
            changeView,
        },
        availableEntities,
        availableSegments,
        availableClientSegments,
        client,
        entity,
        segment,
        selectedAccountLevel,
        subEntity,
    }), [
        availableEntities,
        availableSegments,
        availableClientSegments,
        changeView,
        client,
        entity,
        segment,
        selectedAccountLevel,
        setNewClientEntity,
        subEntity,
    ]);
    useEffect(() => {
        userStorage.clearAll();
        userStorage.setUserId(userId);
        if (defaultClient) {
            userStorage.setClient(defaultClient);
        }
        if (defaultEntity && defaultClient) {
            userStorage.setEntity(defaultEntity);
        }
        if (defaultSegment && defaultEntity && defaultClient) {
            userStorage.setSegment(defaultSegment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(CurrentClientManagerContext.Provider, { value: values }, children));
};
